import {
  ClaimFormSubmission,
  ClaimLookupsDto,
  ClaimResponseDto,
  CreateClaimDto,
} from "../types/claim";
import { PaginatedList, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

function buildClaimFormData(
  dto: CreateClaimDto,
  files?: File[],
  fileCategories?: string[],
  deletedDocumentIds?: number[]
): FormData {
  const formData = new FormData();

  formData.append("DateOfLoss", dto.dateOfLoss);
  formData.append("Status", String(dto.status));

  if (dto.city) formData.append("City", dto.city);
  if (dto.state) formData.append("State", dto.state);
  if (dto.claimCompanyId != null) formData.append("ClaimCompanyId", String(dto.claimCompanyId));
  if (dto.claimCompanyName) formData.append("ClaimCompanyName", dto.claimCompanyName);
  if (dto.assignedToUserId != null) formData.append("AssignedToUserId", String(dto.assignedToUserId));
  if (dto.driverEmployeeId != null) formData.append("DriverEmployeeId", String(dto.driverEmployeeId));
  if (dto.vehicleId != null) formData.append("VehicleId", String(dto.vehicleId));
  if (dto.trailerId != null) formData.append("TrailerId", String(dto.trailerId));
  if (dto.description) formData.append("Description", dto.description);
  if (dto.claimNumber) formData.append("ClaimNumber", dto.claimNumber);
  if (dto.note) formData.append("Note", dto.note);
  if (dto.adjusterName) formData.append("AdjusterName", dto.adjusterName);
  if (dto.adjusterPhone) formData.append("AdjusterPhone", dto.adjusterPhone);
  if (dto.adjusterEmail) formData.append("AdjusterEmail", dto.adjusterEmail);
  if (dto.autoLB != null) formData.append("AutoLB", String(dto.autoLB));
  if (dto.policeReportDescription) formData.append("PoliceReportDescription", dto.policeReportDescription);
  if (dto.totalEstimate != null) formData.append("TotalEstimate", String(dto.totalEstimate));
  if (dto.truckEstimate != null) formData.append("TruckEstimate", String(dto.truckEstimate));
  if (dto.trailerEstimate != null) formData.append("TrailerEstimate", String(dto.trailerEstimate));
  if (dto.driverCharged != null) formData.append("DriverCharged", String(dto.driverCharged));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("Files", file);
    });
    if (fileCategories) {
      formData.append("FileCategories", fileCategories.join(","));
    }
  }

  if (deletedDocumentIds && deletedDocumentIds.length > 0) {
    formData.append("DeletedDocumentIds", deletedDocumentIds.join(","));
  }

  return formData;
}

export const claimsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getClaimsPaginated: builder.query<
      PaginatedList<ClaimResponseDto>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
        };

        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params[`filters[${key}]`] = value;
            }
          });
        }

        return {
          url: "claims",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["Claims"],
    }),

    getClaimLookups: builder.query<ClaimLookupsDto, void>({
      query: () => "claims/lookups",
      transformResponse: unwrapApiResponse,
      providesTags: ["Claims"],
    }),

    getClaimById: builder.query<ClaimResponseDto, number>({
      query: (id) => `claims/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: "Claims", id }],
    }),

    createClaim: builder.mutation<ClaimResponseDto, ClaimFormSubmission>({
      query: ({ dto, files, fileCategories }) => ({
        url: "claims",
        method: "POST",
        body: buildClaimFormData(dto, files, fileCategories),
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Claims", "Documents"],
    }),

    updateClaim: builder.mutation<
      ClaimResponseDto,
      { id: number } & ClaimFormSubmission
    >({
      query: ({ id, dto, files, fileCategories, deletedDocumentIds }) => ({
        url: `claims/${id}`,
        method: "PUT",
        body: buildClaimFormData(dto, files, fileCategories, deletedDocumentIds),
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result, error, { id }) => [
        "Claims",
        { type: "Claims", id },
        "Documents",
      ],
    }),

    deleteClaim: builder.mutation<void, number>({
      query: (id) => ({
        url: `claims/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Claims"],
    }),
  }),
});

export const {
  useGetClaimsPaginatedQuery,
  useGetClaimLookupsQuery,
  useGetClaimByIdQuery,
  useCreateClaimMutation,
  useUpdateClaimMutation,
  useDeleteClaimMutation,
} = claimsApi;
